import React, { useState } from "react";
import { BookOpen, GripVertical, X } from "lucide-react";
import { useTran } from "@/hooks";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ── Sortable Row ──────────────────────────────────────────────────────────────
const QuestionRow = ({ question, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question._id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners} // ✅ whole div is draggable
      className={cn(
        "flex items-center gap-3 p-3 border rounded-lg transition-colors",
        "bg-white hover:bg-gray-50 border-gray-200",
        "dark:bg-gray-800 dark:hover:bg-gray-700/60 dark:border-gray-700",
        "cursor-grab active:cursor-grabbing select-none", // ✅ grab cursor on entire row
        isDragging
          ? "opacity-40 shadow-lg border-indigo-300 dark:border-indigo-600"
          : "",
      )}
    >
      {/* ── Drag Icon (visual only) ── */}
      <div className="shrink-0 p-0.5 rounded text-gray-300 dark:text-gray-600">
        <GripVertical className="w-4 h-4" />
      </div>

      {/* ── Index ── */}
      <span className="text-sm font-medium w-5 shrink-0 text-gray-400 dark:text-gray-500">
        {index + 1}.
      </span>

      {/* ── Question Text + Category ── */}
      <div className="flex-1 min-w-0">
        <p className="text-sm line-clamp-1 text-gray-800 dark:text-gray-200">
          {question.questionText}
        </p>
        {question.category && (
          <p className="text-xs mt-0.5 text-gray-400 dark:text-gray-500">
            {question.category}
          </p>
        )}
      </div>

      {/* ── Type Badge ── */}
      <span className="rounded-full px-2 py-1 text-[11px] font-semibold capitalize bg-primary/10 text-primary dark:bg-primary/20">
        {question.questionType?.replace(/_/g, " ") || "text"}
      </span>

      {/* ── Remove Button ── */}
      {/* ✅ stopPropagation prevents drag from firing when clicking X */}
      <div onPointerDown={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onRemove(question._id)}
          className={cn(
            "flex items-center justify-center w-6 h-6 rounded-md shrink-0",
            "text-gray-300 hover:text-red-500 hover:bg-red-500/10",
            "dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-500/15",
            "transition-colors cursor-pointer", // ✅ pointer cursor on button
          )}
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

// ── Drag Overlay Card (ghost while dragging) ──────────────────────────────────
const DragOverlayCard = ({ question }) => (
  <div
    className={cn(
      "flex items-center gap-3 p-3 border rounded-lg",
      "bg-white border-indigo-300 shadow-xl opacity-95",
      "dark:bg-gray-800 dark:border-indigo-500",
    )}
  >
    <div className="shrink-0 p-0.5 text-gray-400">
      <GripVertical className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm line-clamp-1 text-gray-800 dark:text-gray-200">
        {question?.questionText}
      </p>
    </div>
    <span className="rounded-full px-2 py-1 text-[11px] font-semibold capitalize bg-primary/10 text-primary">
      {question?.questionType?.replace(/_/g, " ") || "text"}
    </span>
  </div>
);

// ── Empty State ───────────────────────────────────────────────────────────────
const EmptyState = ({ t, onOpenLibrary }) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center py-16 gap-3",
      "border-2 border-dashed rounded-xl",
      "border-gray-200 dark:border-gray-700",
    )}
  >
    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
      <BookOpen className="w-5 h-5 text-gray-400 dark:text-gray-500" />
    </div>
    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
      {t("No questions yet")}
    </p>
    <p className="text-xs text-center max-w-xs text-gray-400 dark:text-gray-500">
      {t('Click "Question Library" to pick existing questions')}
    </p>
    <button
      type="button"
      onClick={onOpenLibrary}
      className="text-xs font-medium transition-colors text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
    >
      {t("Browse Question Library →")}
    </button>
  </div>
);

// ── Main Card ─────────────────────────────────────────────────────────────────
const SurveyQuestionLibraryCard = ({
  selectedQuestions = [],
  onOpenLibrary,
  onRemoveQuestion,
  onReorderQuestions,
  error,
}) => {
  const { t } = useTran();
  const [activeQuestion, setActiveQuestion] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }, // ✅ prevent accidental drag on click
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event) => {
    const q = selectedQuestions.find(
      (q) => q._id.toString() === event.active.id,
    );
    setActiveQuestion(q ?? null);
  };

  const handleDragEnd = (event) => {
    setActiveQuestion(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = selectedQuestions.findIndex(
      (q) => q._id.toString() === active.id,
    );
    const newIndex = selectedQuestions.findIndex(
      (q) => q._id.toString() === over.id,
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      onReorderQuestions(arrayMove(selectedQuestions, oldIndex, newIndex));
    }
  };

  const handleDragCancel = () => setActiveQuestion(null);

  return (
    <Card className="space-y-1 border border-border bg-card p-5 shadow-sm lg:col-span-2">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {t("Questions")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("Define scored questions grouped by category.")}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="flex items-center gap-1"
          onClick={onOpenLibrary}
        >
          <BookOpen className="h-4 w-4" />
          {t("Question Library")}
        </Button>
      </div>

      {/* ── Error ── */}
      {error && (
        <p className="text-xs font-medium text-red-500 dark:text-red-400">
          {error}
        </p>
      )}

      {/* ── Count ── */}
      {selectedQuestions.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {selectedQuestions.length}{" "}
          {selectedQuestions.length === 1 ? t("question") : t("questions")}{" "}
          {t("added")}
          <span className="ml-1 text-gray-400 dark:text-gray-500">
            · {t("drag row to reorder")}
          </span>
        </p>
      )}

      {/* ── Empty / Sortable List ── */}
      {selectedQuestions.length === 0 ? (
        <EmptyState t={t} onOpenLibrary={onOpenLibrary} />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={selectedQuestions.map((q) => q._id.toString())}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {selectedQuestions.map((q, index) => (
                <QuestionRow
                  key={q._id}
                  question={q}
                  index={index}
                  onRemove={onRemoveQuestion}
                />
              ))}
            </div>
          </SortableContext>

          {/* ✅ Floating ghost card while dragging */}
          <DragOverlay>
            {activeQuestion && <DragOverlayCard question={activeQuestion} />}
          </DragOverlay>
        </DndContext>
      )}
    </Card>
  );
};

export default SurveyQuestionLibraryCard;
